import { clsx } from "@/helpers/clsx";
import Link from "next/link";
import * as React from "react";

const baseStyles = {
  solid:
    "group inline-flex items-center justify-center rounded-full py-2.5 px-3 text-sm font-semibold focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 active:scale-[0.97] transition-all ease-in-out focsuable duration-300 ease-out-quad",
  outline:
    "group inline-flex ring-1 items-center justify-center rounded-full py-2 px-4 text-sm focus:outline-none focusable",
};

const variantStyles = {
  solid: {
    slate:
      "bg-slate-900 text-white hover:bg-slate-700 hover:text-slate-100 active:bg-slate-800 active:text-slate-300",
    blue: "bg-blue-600 text-white hover:text-slate-100 hover:bg-blue-500 active:bg-blue-800 active:text-blue-100",
    white:
      "bg-white text-slate-900 hover:bg-emerald-50 active:bg-emerald-200 active:text-slate-600 rounded-lg",
    violet:
      "bg-gradient-to-br from-violet-500 via-violet-800 to-blue-900 text-white shadow-inner active:animate-pulse duration-300 hover:shadow-circle hover:shadow-violet-600",
    primary:
      "rounded-lg bg-emerald-600 text-white shadow-sm shadow-black/20 hover:shadow-lg border border-transparent bg-btn-primary hover:border-primary-300/50",
    primary2:
      "rounded-lg bg-emerald-600 text-white shadow-sm shadow-black/20 hover:shadow-lg border border-transparent bg-btn-primary hover:border-primary-300/50 bg-gradient-to-b from-primary-600 to-primary-700 !shadow-[inset_0_0.5px_0_#ffffff50,inset_0_-0.5px_0_#00000050]",
    secondary:
      "rounded-lg text-white shadow-sm shadow-black/20 hover:shadow-lg border border-transparent bg-btn-secondary hover:border-neutral-400",
    indigo:
      "rounded-lg text-white shadow-sm shadow-black/20 hover:shadow-lg border border-transparent bg-btn-indigo-2",
    text: "hover:bg-black rounded-lg hover:shadow-sm hover:shadow-black/20 transition-colors border border-transparent hover:bg-btn-secondary-dark",
  },
  outline: {
    slate:
      "ring-slate-200 text-slate-700 hover:text-slate-900 hover:ring-slate-300 active:bg-slate-100 active:text-slate-600",
    white:
      "ring-slate-700 text-white hover:ring-slate-500 active:ring-slate-700 active:text-slate-400",
  },
};

type ButtonPropsBase = {
  variant?: keyof typeof variantStyles;
  color?: keyof typeof variantStyles.solid | "custom";
  className?: string;
  external?: boolean;
};

type ButtonWithLink = ButtonPropsBase & {
  href: string;
} & React.ComponentPropsWithoutRef<typeof Link>;

type ButtonWithButton = ButtonPropsBase & {
  href?: never;
} & React.ComponentPropsWithoutRef<"button">;

type ButtonProps = ButtonWithLink | ButtonWithButton;

export const Button = ({
  ref,
  variant = "solid",
  color = "secondary",
  className,
  external,
  ...props
}: ButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  className = clsx(
    "!leading-none no-drag",
    baseStyles[variant],
    // @ts-ignore
    variantStyles[variant][color],
    className,
    isButton(props) && props.disabled && "pointer-events-none opacity-50",
  );

  if (isLink(props)) {
    if (external) {
      return <a className={className} {...(props as any)} ref={ref as any} />;
    }
    return <Link className={className} {...props} ref={ref as any} />;
  } else if (isButton(props)) {
    return <button className={className} {...props} ref={ref} />;
  }

  return null;
};

Button.displayName = "Button";

const isLink = (props: ButtonProps): props is ButtonWithLink => "href" in props;
const isButton = (props: ButtonProps): props is ButtonWithButton =>
  !isLink(props);
